'use client';

/**
 * Settings and accessibility.
 *
 * Accessibility options are first-class here, not buried: the children most
 * helped by a dyslexia-friendly reading mode or a colour-blind-safe palette
 * are exactly the ones least likely to go hunting for a toggle. Each option
 * says plainly what it does, in language a ten-year-old can read.
 */
import React, { useRef, useState } from 'react';
import { Download, Upload, Trash2, RotateCcw } from 'lucide-react';
import { useKids } from '../context/KidsProvider';
import { kidsAudio, speechSupported } from '../lib/audio';
import type { KidSettings } from '../types';
import { LoadingBlock } from './ui';

interface Choice<T extends keyof KidSettings> {
  key: T;
  label: string;
  help: string;
  options: { value: KidSettings[T]; label: string; hint?: string }[];
}

const CHOICES: Choice<'scheme' | 'motion' | 'reading' | 'contrast' | 'vision'>[] = [
  {
    key: 'scheme',
    label: 'Colours',
    help: 'Dark mode is easier on the eyes in a dim room.',
    options: [
      { value: 'light', label: '☀️ Light' },
      { value: 'dark', label: '🌙 Dark' },
    ],
  },
  {
    key: 'motion',
    label: 'Movement',
    help: 'Calm mode removes bouncing, confetti and pulsing.',
    options: [
      { value: 'full', label: '🎉 Full' },
      { value: 'calm', label: '🧘 Calm' },
    ],
  },
  {
    key: 'reading',
    label: 'Reading',
    help: 'Easy-read spaces out letters, words and lines. Many people with dyslexia find it clearer.',
    options: [
      { value: 'default', label: 'Normal' },
      { value: 'dyslexia', label: '📖 Easy-read' },
    ],
  },
  {
    key: 'contrast',
    label: 'Contrast',
    help: 'High contrast uses pure black and white with heavier outlines.',
    options: [
      { value: 'default', label: 'Normal' },
      { value: 'high', label: '◐ High' },
    ],
  },
  {
    key: 'vision',
    label: 'Colour vision',
    help: 'Colour-friendly swaps the green/red right-and-wrong colours for blue and orange.',
    options: [
      { value: 'default', label: 'Normal' },
      { value: 'colorblind', label: '🎨 Colour-friendly' },
    ],
  },
];

export default function SettingsPanel() {
  const {
    hydrated,
    profile,
    store,
    settings,
    updateSettings,
    renameProfile,
    resetProfile,
    deleteProfile,
    exportData,
    importData,
  } = useKids();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  if (!hydrated || !profile) return <LoadingBlock height={400} />;

  const download = () => {
    const blob = new Blob([exportData()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `typeswift-kids-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const upload = async (file: File) => {
    const text = await file.text();
    setMessage(
      importData(text)
        ? 'Progress restored from your backup.'
        : 'That file could not be read. Make sure it is a TypeSwift backup.'
    );
  };

  return (
    <div className="grid gap-6">
      {message && (
        <p
          role="status"
          className="k-card p-3 text-sm font-extrabold"
          style={{ borderColor: 'var(--accent)' }}
        >
          {message}
        </p>
      )}

      {/* ── name ──────────────────────────────────────────────────────── */}
      <section className="k-card p-5">
        <h2 className="text-base mb-3">Your name</h2>
        <input
          defaultValue={profile.name}
          onBlur={(e) => renameProfile(profile.id, e.target.value)}
          maxLength={24}
          aria-label="Your display name"
          className="w-full max-w-xs px-4 font-bold rounded-2xl outline-none"
          style={{
            height: 50,
            background: 'var(--input)',
            border: '2px solid var(--border-strong)',
            color: 'var(--foreground)',
          }}
        />
      </section>

      {/* ── sound ─────────────────────────────────────────────────────── */}
      <section className="k-card p-5">
        <h2 className="text-base mb-1">Sound</h2>
        <p className="text-sm text-muted-foreground mb-4">
          All sounds are made by your browser — nothing is downloaded.
        </p>

        <Toggle
          label="Typing sounds"
          help="A tiny click for each key, a buzz when a key is wrong."
          checked={settings.sound}
          onChange={(sound) => {
            kidsAudio.setEnabled(sound);
            updateSettings({ sound });
          }}
        />

        <div className="mt-4">
          <label htmlFor="kids-volume" className="k-eyebrow block mb-2">
            Volume
          </label>
          <input
            id="kids-volume"
            type="range"
            min={0}
            max={100}
            step={5}
            value={Math.round(settings.volume * 100)}
            onChange={(e) => {
              const volume = Number(e.target.value) / 100;
              // Apply to the engine at once so the preview plays at the level
              // being chosen, not the previously saved one.
              kidsAudio.configure({ volume });
              updateSettings({ volume });
            }}
            // pointerup covers mouse and touch; mouseup alone missed tablets.
            onPointerUp={() => kidsAudio.play('correct')}
            className="w-full max-w-xs"
            disabled={!settings.sound}
          />
        </div>

        <div className="mt-4">
          <Toggle
            label="Spoken help"
            help={
              speechSupported()
                ? 'Reads the coach tip out loud at the start of each lesson, using your browser’s voice.'
                : 'Your browser does not support spoken help.'
            }
            checked={settings.voice}
            disabled={!speechSupported()}
            onChange={(voice) => {
              updateSettings({ voice });
              if (voice) {
                kidsAudio.configure({ voice: true });
                kidsAudio.speak('Spoken help is on. I will read each tip out loud.');
              }
            }}
          />
        </div>
      </section>

      {/* ── lesson helpers ────────────────────────────────────────────── */}
      <section className="k-card p-5">
        <h2 className="text-base mb-3">Lesson helpers</h2>
        <Toggle
          label="Show the keyboard"
          help="The on-screen keyboard that lights up the next key."
          checked={settings.keyboard}
          onChange={(keyboard) => updateSettings({ keyboard })}
        />
        <div className="mt-3">
          <Toggle
            label="Show the hands"
            help="The hand picture that raises the correct finger."
            checked={settings.hands}
            onChange={(hands) => updateSettings({ hands })}
          />
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Turning both off is good practice once you know a level — challenge lessons hide them
          anyway.
        </p>
      </section>

      {/* ── accessibility ─────────────────────────────────────────────── */}
      <section className="k-card p-5">
        <h2 className="text-base mb-1">How things look</h2>
        <p className="text-sm text-muted-foreground mb-4">
          These change straight away, so try them and keep what feels best.
        </p>
        <div className="grid gap-5">
          {CHOICES.map((choice) => (
            <fieldset key={choice.key}>
              <legend className="k-eyebrow">{choice.label}</legend>
              <p className="text-xs text-muted-foreground mt-0.5 mb-2">{choice.help}</p>
              <div className="flex flex-wrap gap-2">
                {choice.options.map((option) => (
                  <button
                    key={String(option.value)}
                    type="button"
                    onClick={() =>
                      updateSettings({ [choice.key]: option.value } as Partial<KidSettings>)
                    }
                    aria-pressed={settings[choice.key] === option.value}
                    className="k-btn k-btn-secondary k-btn-sm"
                    style={
                      settings[choice.key] === option.value
                        ? { borderColor: 'var(--accent)', color: 'var(--accent)' }
                        : undefined
                    }
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </fieldset>
          ))}
        </div>
      </section>

      {/* ── data ──────────────────────────────────────────────────────── */}
      <section className="k-card p-5">
        <h2 className="text-base mb-1">Your data</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Everything is stored in this browser only. Nothing has been sent anywhere. Back it up
          before changing computers or clearing your browsing data.
        </p>

        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={download} className="k-btn k-btn-secondary k-btn-sm">
            <Download size={16} aria-hidden="true" /> Save a backup
          </button>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="k-btn k-btn-secondary k-btn-sm"
          >
            <Upload size={16} aria-hidden="true" /> Restore a backup
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            className="sr-only"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void upload(file);
              e.target.value = '';
            }}
          />
        </div>

        <div className="mt-6 pt-5 grid gap-3" style={{ borderTop: '2px solid var(--border)' }}>
          {confirmReset ? (
            <ConfirmRow
              text="Erase all lessons, stars, coins and badges for this typist? The name and look are kept."
              confirmLabel="Yes, start over"
              onConfirm={() => {
                resetProfile(profile.id);
                setConfirmReset(false);
                setMessage('Progress reset. The journey starts again at Level 1.');
              }}
              onCancel={() => setConfirmReset(false)}
            />
          ) : (
            <button
              type="button"
              onClick={() => setConfirmReset(true)}
              className="k-btn k-btn-secondary k-btn-sm self-start"
            >
              <RotateCcw size={16} aria-hidden="true" /> Start the journey over
            </button>
          )}

          {confirmDelete ? (
            <ConfirmRow
              text={`Permanently delete “${profile.name}” and everything they have done? This cannot be undone.`}
              confirmLabel="Delete for good"
              danger
              onConfirm={() => {
                deleteProfile(profile.id);
                setConfirmDelete(false);
              }}
              onCancel={() => setConfirmDelete(false)}
            />
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="k-btn k-btn-secondary k-btn-sm self-start"
              style={{ color: 'var(--incorrect)', borderColor: 'var(--incorrect)' }}
            >
              <Trash2 size={16} aria-hidden="true" /> Delete this typist
            </button>
          )}
        </div>

        <p className="text-xs text-muted-foreground mt-4">
          {store.profiles.length} typist{store.profiles.length === 1 ? '' : 's'} saved on this
          device.
        </p>
      </section>
    </div>
  );
}

function Toggle({
  label,
  help,
  checked,
  onChange,
  disabled = false,
}: {
  label: string;
  help: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className={`flex items-start gap-3 cursor-pointer ${disabled ? 'opacity-50' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 shrink-0"
        style={{ width: 22, height: 22, accentColor: 'var(--accent)' }}
      />
      <span>
        <span className="block font-extrabold">{label}</span>
        <span className="block text-sm text-muted-foreground">{help}</span>
      </span>
    </label>
  );
}

function ConfirmRow({
  text,
  confirmLabel,
  onConfirm,
  onCancel,
  danger = false,
}: {
  text: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  danger?: boolean;
}) {
  return (
    <div
      className="p-4 rounded-2xl"
      style={{
        background: danger ? 'var(--incorrect-soft)' : 'var(--muted)',
        border: `2px solid ${danger ? 'var(--incorrect)' : 'var(--border-strong)'}`,
      }}
      role="alertdialog"
      aria-label={confirmLabel}
    >
      <p className="text-sm font-bold">{text}</p>
      <div className="flex flex-wrap gap-2 mt-3">
        <button
          type="button"
          onClick={onConfirm}
          className="k-btn k-btn-sm"
          style={{
            background: danger ? 'var(--incorrect)' : 'var(--foreground)',
            color: 'var(--background)',
          }}
        >
          {confirmLabel}
        </button>
        <button type="button" onClick={onCancel} className="k-btn k-btn-ghost k-btn-sm">
          Cancel
        </button>
      </div>
    </div>
  );
}
